import { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-[#121212] border border-white/10 rounded px-3 py-2 text-sm text-foreground";

export default function DefectComments({ token, pin, pinOk, defectId, comments, onRefresh, onNeedPin }) {
  const mine = comments.filter((c) => c.defect_id === defectId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author_name: "", author_email: "", body: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.body.trim()) return toast.error("Write a question first");
    setBusy(true);
    try {
      await axios.post(`${API}/share/${token}/defects/${defectId}/comments`, { pin, ...form });
      setForm((f) => ({ ...f, body: "" }));
      setOpen(false); toast.success("Question sent to Zentara"); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`comments-${defectId}`}>
      <div className="flex items-center justify-between">
        <p className="label-caps text-[#C5A24A] flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Questions &amp; comments{mine.length ? ` (${mine.length})` : ""}</p>
        {pinOk ? (
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} className="h-7 gap-1 border-white/15 bg-transparent text-xs hover:bg-white/5" data-testid={`ask-${defectId}`}>Ask a question</Button>
        ) : (
          <button onClick={onNeedPin} className="text-xs text-[#C5A24A] hover:underline">Enter access code to ask</button>
        )}
      </div>

      {mine.length > 0 && (
        <div className="mt-3 space-y-2">
          {mine.map((c) => (
            <div key={c.id} className={`text-sm rounded p-2.5 ${c.role === "zentara" ? "bg-[#1a1710] border border-[#C5A24A]/30" : "bg-[#0A0A0A] border border-white/10"}`} data-testid={`comment-${c.id}`}>
              <p className="text-[11px] text-muted-foreground">{c.role === "zentara" ? "Zentara" : (c.author_name || "Client")} · {(c.created_at || "").slice(0, 10)}</p>
              <p className="mt-0.5">{c.body}</p>
            </div>
          ))}
        </div>
      )}

      {pinOk && open && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Your name" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className={inputCls} data-testid={`comment-name-${defectId}`} />
            <input placeholder="Your email (optional)" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} className={inputCls} />
          </div>
          <textarea placeholder="Ask a question about this defect…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} min-h-[60px]`} data-testid={`comment-body-${defectId}`} />
          <Button size="sm" onClick={submit} disabled={busy} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black text-xs gap-1" data-testid={`send-comment-${defectId}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3" /> Send to Zentara</>}</Button>
        </div>
      )}
    </div>
  );
}
